// 🔬 B242 MVP3a (AI_112) — PLANO DE COORTE PROPOSTO POR IA (camada determinística)
// ─────────────────────────────────────────────────────────────────────────────
// REGRA DE OURO do B242: a IA PROPÕE um plano (quais filtros); o CÓDIGO valida e
// CONTA. Este módulo é a metade "código decide" — puro e testável, SEM rede e SEM
// Firebase (a chamada ao /api/analyze fica no componente). Ele recebe o plano
// BRUTO da IA e o sanea contra o catálogo de campos: descarta campo inexistente,
// operador incompatível com o tipo e valor categórico fora das opções — em vez de
// confiar cegamente (mesma postura do comparador de nomes do B241: IA lê, código
// decide). Nada conta sem o médico confirmar (a UI só aplica o plano validado).
import { CampoPesquisa, Filtro, Operador, TipoCampo } from "../types";
import { paraNumero } from "./estatisticasDescritivas";

// ── Operadores válidos por tipo (FONTE ÚNICA, espelhada na UI) ────────────────
// O construtor de filtros (AbaPesquisaClinica) importa estas funções para que um
// filtro proposto pela IA seja sempre editável no mesmo conjunto de operadores.
export function operadoresDe(tipo: TipoCampo): Operador[] {
  if (tipo === "numerico")
    return ["igual", "maior", "maiorIgual", "menor", "menorIgual", "entre", "presente", "ausente"];
  if (tipo === "texto") return ["contem", "presente", "ausente"];
  // categorico / booleano
  return ["igual", "diferente", "presente", "ausente"];
}

export function precisaValor(op: Operador): boolean {
  return op !== "presente" && op !== "ausente";
}

// ── Catálogo enxuto enviado à IA (SÓ metadados de campo; NUNCA valores/dados) ──
export interface CampoCatalogoIA {
  chave: string;
  label: string;
  tipo: TipoCampo;
  unidade?: string;
  opcoes?: { valor: string; label: string }[];
  operadores: Operador[];
}

export function catalogoParaIA(campos: CampoPesquisa[]): CampoCatalogoIA[] {
  return campos.map((c) => ({
    chave: c.chave,
    label: c.label,
    tipo: c.tipo,
    ...(c.unidade ? { unidade: c.unidade } : {}),
    ...(c.opcoes ? { opcoes: c.opcoes.map((o) => ({ valor: o.valor, label: o.label })) } : {}),
    operadores: operadoresDe(c.tipo),
  }));
}

// ── Forma do plano BRUTO devolvido pela IA (tudo opcional/defensivo) ──────────
interface FiltroBruto {
  campo?: unknown;
  operador?: unknown;
  valor?: unknown;
  valor2?: unknown;
}

export interface PlanoIABruto {
  filtros?: FiltroBruto[];
  // B242 MVP3a — proxies estruturados p/ termos que a IA não mapeou (cada um é
  // um FILTRO sobre um campo EXISTENTE + um motivo). Passa pela mesma validação.
  aproximacoes?: Array<FiltroBruto & { motivo?: unknown }>;
  conector?: unknown;
  estratificarPor?: unknown;
  naoMapeado?: unknown;
  resumo?: unknown;
}

// Aproximação validada: um filtro real (sobre campo do catálogo) + por que ele
// aproxima o termo não mapeado. O médico decide se adiciona (1 clique).
export interface AproximacaoValidada {
  filtro: Filtro;
  motivo: string;
}

export interface PlanoIAValidado {
  filtros: Filtro[];
  conector: "E" | "OU";
  estratificarPor: string; // "" = sem estratificação
  resumo: string;
  naoMapeado: string[]; // termos que a IA declarou NÃO ter conseguido mapear
  aproximacoes: AproximacaoValidada[]; // proxies estruturados sugeridos (clicáveis)
  descartados: string[]; // itens removidos pelo validador (motivo legível)
}

// Normaliza texto p/ comparação (minúsculas, sem acento, trim) — igual ao motor.
function norm(v: unknown): string {
  return String(v ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();
}

// Acha o campo do catálogo a partir de uma referência da IA: tenta a chave exata,
// depois a chave/rótulo normalizados (tolerante a "MELD" em vez de "meld").
function acharCampo(campos: CampoPesquisa[], ref: unknown): CampoPesquisa | undefined {
  const r = norm(ref);
  if (!r) return undefined;
  return (
    campos.find((c) => c.chave === ref) ||
    campos.find((c) => norm(c.chave) === r) ||
    campos.find((c) => norm(c.label) === r)
  );
}

// Mapeia um valor categórico/booleano da IA para o VALOR canônico do catálogo.
// Retorna null se não casar com nenhuma opção (→ descartado).
function canonizarValorCategorico(
  campo: CampoPesquisa,
  valor: unknown
): string | null {
  if (campo.tipo === "booleano") {
    const v = norm(valor);
    if (["true", "sim", "1", "verdadeiro", "presente"].includes(v)) return "true";
    if (["false", "nao", "0", "falso", "ausente"].includes(v)) return "false";
    return null;
  }
  // categórico sem opções curadas (não há hoje, mas defensivo): aceita o texto cru.
  if (!campo.opcoes || campo.opcoes.length === 0) {
    const v = String(valor ?? "").trim();
    return v === "" ? null : v;
  }
  // categórico com opções: casa por valor OU rótulo (normalizado).
  const alvo = norm(valor);
  const opt =
    campo.opcoes.find((o) => norm(o.valor) === alvo) ||
    campo.opcoes.find((o) => norm(o.label) === alvo);
  return opt ? opt.valor : null;
}

// Valida UM filtro bruto da IA contra o catálogo. Retorna o Filtro saneado OU um
// motivo de descarte legível. Compartilhado por `filtros` e `aproximacoes`.
function validarFiltroBruto(
  f: FiltroBruto,
  campos: CampoPesquisa[]
): { filtro?: Filtro; descarte?: string } {
  const campo = acharCampo(campos, f?.campo);
  if (!campo) {
    return { descarte: `"${String(f?.campo ?? "?")}" — campo não reconhecido no catálogo` };
  }

  const op = String(f?.operador ?? "") as Operador;
  if (!operadoresDe(campo.tipo).includes(op)) {
    return {
      descarte: `${campo.label} — condição "${String(f?.operador ?? "?")}" não se aplica a este campo`,
    };
  }

  // Operadores sem valor.
  if (!precisaValor(op)) return { filtro: { campo: campo.chave, operador: op } };

  // "entre": exige dois limites numéricos.
  if (op === "entre") {
    const a = paraNumero(f?.valor);
    const b = paraNumero(f?.valor2);
    if (a === null || b === null) return { descarte: `${campo.label} — "entre" precisa de dois números` };
    return { filtro: { campo: campo.chave, operador: op, valor: String(a), valor2: String(b) } };
  }

  // Comparadores numéricos.
  if (campo.tipo === "numerico") {
    const a = paraNumero(f?.valor);
    if (a === null) return { descarte: `${campo.label} — valor não numérico` };
    return { filtro: { campo: campo.chave, operador: op, valor: String(a) } };
  }

  // Texto (contém).
  if (campo.tipo === "texto") {
    const v = String(f?.valor ?? "").trim();
    if (v === "") return { descarte: `${campo.label} — texto a procurar vazio` };
    return { filtro: { campo: campo.chave, operador: op, valor: v } };
  }

  // Categórico / booleano (igual/diferente).
  const v = canonizarValorCategorico(campo, f?.valor);
  if (v === null) {
    return { descarte: `${campo.label} — valor "${String(f?.valor ?? "?")}" não é uma opção válida` };
  }
  return { filtro: { campo: campo.chave, operador: op, valor: v } };
}

/**
 * Sanea o plano BRUTO da IA contra o catálogo. PURO e determinístico.
 * - campo inexistente → descartado
 * - operador incompatível com o tipo → descartado
 * - valor numérico não-numérico, ou categórico fora das opções → descartado
 * - "entre" sem os dois limites → descartado
 * Cada descarte registra um motivo legível em `descartados` (sem dado de paciente).
 * Aproximações (proxies p/ termos não mapeados) passam pela MESMA validação; as
 * inválidas são silenciosamente ignoradas (são apenas sugestões opcionais).
 */
export function validarPlanoIA(
  bruto: PlanoIABruto | null | undefined,
  campos: CampoPesquisa[]
): PlanoIAValidado {
  const descartados: string[] = [];
  const filtros: Filtro[] = [];

  const conector: "E" | "OU" = ["ou", "or", "qualquer"].includes(norm(bruto?.conector))
    ? "OU"
    : "E";

  const brutos = Array.isArray(bruto?.filtros) ? bruto!.filtros! : [];
  for (const f of brutos) {
    const { filtro, descarte } = validarFiltroBruto(f, campos);
    if (filtro) filtros.push(filtro);
    else if (descarte) descartados.push(descarte);
  }

  // Aproximações: proxies estruturados p/ o que não foi mapeado. Mesma validação;
  // inválidas são descartadas em silêncio (não poluem `descartados`).
  const aproximacoes: AproximacaoValidada[] = [];
  const aproxBrutas = Array.isArray(bruto?.aproximacoes) ? bruto!.aproximacoes! : [];
  for (const a of aproxBrutas) {
    const { filtro } = validarFiltroBruto(a, campos);
    if (filtro) {
      aproximacoes.push({
        filtro,
        motivo: typeof a?.motivo === "string" ? a.motivo.trim() : "",
      });
    }
  }

  // Estratificação: só vale para campo categórico/booleano existente.
  let estratificarPor = "";
  if (bruto?.estratificarPor) {
    const campo = acharCampo(campos, bruto.estratificarPor);
    if (campo && (campo.tipo === "categorico" || campo.tipo === "booleano")) {
      estratificarPor = campo.chave;
    } else if (campo) {
      descartados.push(`Estratificar por "${campo.label}" — só dá p/ campos de categoria`);
    }
  }

  const naoMapeado = Array.isArray(bruto?.naoMapeado)
    ? bruto!.naoMapeado!.map((x) => String(x)).filter((x) => x.trim() !== "")
    : [];

  const resumo = typeof bruto?.resumo === "string" ? bruto.resumo.trim() : "";

  return { filtros, conector, estratificarPor, resumo, naoMapeado, aproximacoes, descartados };
}
